import { notification } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

export type Orientation = {
    alpha: number | null; // 0..360
    beta: number | null; // -180..180
    gamma: number | null; // -90..90
};

export type Axis = keyof Orientation;

export type Range = {
    min: number;
    max: number;
    abs?: boolean; // ✅ нове
};

export type OrientationLimits = Partial<Record<Axis, Range>>;

export type UseDeviceOrientationReturn = ReturnType<
    typeof useDeviceOrientation
>;

type PermissionState = "unknown" | "granted" | "denied";

const inRange = (v: number, min: number, max: number) => v >= min && v <= max;

// Для кутів 0..360, підтримує "wrap" через 0, напр. 350..10
const inRange360 = (v: number, min: number, max: number) => {
    const vv = ((v % 360) + 360) % 360;
    const mn = ((min % 360) + 360) % 360;
    const mx = ((max % 360) + 360) % 360;

    if (mn <= mx) return vv >= mn && vv <= mx;
    return vv >= mn || vv <= mx;
};

const hasAnyNumber = (e: DeviceOrientationEvent) =>
    typeof e.alpha === "number" ||
    typeof e.beta === "number" ||
    typeof e.gamma === "number";

const isValidByLimits = (o: Orientation, limits?: OrientationLimits) => {
    if (!limits || Object.keys(limits).length === 0) return true;

    const axes = Object.keys(limits) as Axis[];
    for (const axis of axes) {
        const r = limits[axis];
        if (!r) continue;

        const raw = o[axis];
        if (typeof raw !== "number") return false;

        const v = r.abs ? Math.abs(raw) : raw;

        const ok =
            axis === "alpha"
                ? inRange360(v, r.min, r.max)
                : inRange(v, r.min, r.max);

        if (!ok) return false;
    }

    return true;
};

export const useDeviceOrientation = (opts: {
    enabled?: boolean;
    limits?: OrientationLimits;
    enableTimeoutMs?: number;
}) => {
    const { enabled = true, limits, enableTimeoutMs = 300 } = opts;

    // supported = "реально працює" (є хоча б один івент з numbers)
    const [supported, setSupported] = useState(false);

    const [permission, setPermission] = useState<PermissionState>("unknown");
    const [isEnabled, setIsEnabled] = useState(false);

    const [ori, setOri] = useState<Orientation>({
        alpha: null,
        beta: null,
        gamma: null,
    });

    const isValid = useMemo(() => isValidByLimits(ori, limits), [ori, limits]);

    // 1) Пасивно визначаємо supported (чи реально прилітають numbers)
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!("DeviceOrientationEvent" in window)) return;

        const probe = (e: DeviceOrientationEvent) => {
            if (hasAnyNumber(e)) {
                setSupported(true);
                window.removeEventListener("deviceorientation", probe, true);
            }
        };

        window.addEventListener("deviceorientation", probe, true);
        return () =>
            window.removeEventListener("deviceorientation", probe, true);
    }, []);

    const enable = useCallback(async () => {
        if (typeof window === "undefined") return false;
        if (!("DeviceOrientationEvent" in window)) return false;

        try {
            const anyDO = DeviceOrientationEvent as unknown as {
                requestPermission?: () => Promise<"granted" | "denied">;
            };

            // iOS permission
            if (typeof anyDO.requestPermission === "function") {
                const res = await anyDO.requestPermission();
                if (res !== "granted") {
                    setPermission("denied");
                    setIsEnabled(false);
                    return false;
                }
                setPermission("granted");
            } else {
                setPermission("granted");
            }

            // чекаємо перший реальний event з numbers (щоб на ПК не було "фейкового enable")
            const ok = await new Promise<boolean>((resolve) => {
                let done = false;

                const on = (e: DeviceOrientationEvent) => {
                    if (done) return;
                    if (!hasAnyNumber(e)) return;

                    done = true;
                    cleanup();
                    resolve(true);
                };

                const cleanup = () => {
                    window.removeEventListener("deviceorientation", on, true);
                    clearTimeout(tid);
                };

                window.addEventListener("deviceorientation", on, true);

                const tid = window.setTimeout(() => {
                    if (done) return;
                    done = true;
                    cleanup();
                    resolve(false);
                }, enableTimeoutMs);
            });

            setSupported(ok);
            setIsEnabled(ok);
            return ok;
        } catch (e) {
            console.error("enable orientation error:", e);
            setPermission("denied");
            setIsEnabled(false);
            notification.error({ message: String(e) });
            return false;
        }
    }, [enableTimeoutMs]);

    const disable = useCallback(() => {
        setIsEnabled(false);
    }, []);

    // 2) Слухаємо орієнтацію тільки якщо enabled && isEnabled && supported
    useEffect(() => {
        if (!enabled) return;
        if (!supported) return;
        if (!isEnabled) return;

        const handler = (e: DeviceOrientationEvent) => {
            const next: Orientation = {
                alpha: typeof e.alpha === "number" ? e.alpha : null,
                beta: typeof e.beta === "number" ? e.beta : null,
                gamma: typeof e.gamma === "number" ? e.gamma : null,
            };

            setOri(next);

            if (!supported && hasAnyNumber(e)) setSupported(true);
        };

        window.addEventListener("deviceorientation", handler, true);
        return () =>
            window.removeEventListener("deviceorientation", handler, true);
    }, [enabled, supported, isEnabled]);

    const reset = useCallback(() => {
        setOri({ alpha: null, beta: null, gamma: null });
    }, []);

    return {
        supported,
        permission,
        enabled: isEnabled,
        ori,

        // валідація
        isValid,

        // керування
        enable,
        disable,
        reset,
    };
};
